/**
 * Remembers what an admin typed in Try it, in this browser only, so the next visit (or another
 * call that takes the same input, like policy_id) starts filled in instead of blank. A stand-in
 * until saved inputs come from the server; it never holds sign-in secrets.
 */

const PREFIX = "orbit.try.";
const MAX_VALUE_LENGTH = 2000;
// Headers that carry credentials are added by Orbit; one typed by hand is still not kept.
const SECRET_NAME = /auth|token|key|secret|password|cookie|session/i;

const storageKey = (integrationId) => `${PREFIX}${integrationId}`;

function readStore(integrationId) {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(integrationId)) || "{}");
    return { calls: parsed.calls || {}, names: parsed.names || {} };
  } catch {
    return { calls: {}, names: {} };
  }
}

function writeStore(integrationId, store) {
  try {
    localStorage.setItem(storageKey(integrationId), JSON.stringify(store));
  } catch {
    // Full or blocked storage only costs the convenience.
  }
}

const isKeepable = (field, value) =>
  typeof value === "string" &&
  value !== "" &&
  value.length <= MAX_VALUE_LENGTH &&
  !(field.location === "header" && SECRET_NAME.test(field.name));

/**
 * The value each field starts with: what was last sent on this call, else what was last sent
 * for an input of the same name on another call, else the example from the API's docs.
 * `remembered` says whether any value came from this browser rather than the docs.
 */
export function startingValues(integrationId, endpoint, exampleOf) {
  const { calls, names } = readStore(integrationId);
  const own = calls[endpoint.id] || {};
  let remembered = false;
  const values = Object.fromEntries(
    endpoint.fields.map((f) => {
      const saved = own[f.key] ?? (f.location !== "body" ? names[f.name] : undefined);
      if (saved !== undefined && isKeepable(f, saved)) {
        remembered = true;
        return [f.key, saved];
      }
      return [f.key, exampleOf(f)];
    })
  );
  return { values, remembered };
}

/** Keeps the inputs of a sent request, per call and by input name. */
export function rememberValues(integrationId, endpoint, values) {
  const store = readStore(integrationId);
  const kept = endpoint.fields.filter((f) => isKeepable(f, values[f.key]));
  store.calls[endpoint.id] = Object.fromEntries(kept.map((f) => [f.key, values[f.key]]));
  kept.filter((f) => f.location !== "body").forEach((f) => {
    store.names[f.name] = values[f.key];
  });
  writeStore(integrationId, store);
}

/** The calls sent at least once from this browser, so "tried" survives a reload. */
export function triedCallIds(integrationId) {
  return new Set(Object.keys(readStore(integrationId).calls));
}

/** Forgets one call's inputs, so it starts again from the docs' examples; it still counts as tried. */
export function forgetValues(integrationId, endpointId) {
  const store = readStore(integrationId);
  store.calls[endpointId] = {};
  writeStore(integrationId, store);
}
